import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, InputNumber, Button, Row, Col, Popconfirm, Checkbox, Progress } from 'antd';
import { DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Block, Floor, ApartmentUnit, Parking } from '@/types/resources/property';
import { addApartmentUnitsBulk, addParkingBulk } from '@/api/properties';
import { useAuth } from '@/contexts/AuthContext';
import { incrementBlockCount, initializeBlockStats } from '@/api/workspaces';

interface BlockFormModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: { block: Partial<Block>; floors: Partial<Floor>[] }) => Promise<string>;
  initialValues?: Partial<Block>;
  title: string;
}

export const BlockFormModal: React.FC<BlockFormModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  initialValues,
  title
}) => {
  const { t } = useTranslation('properties');
  const [form] = Form.useForm();
  const [generatedFloors, setGeneratedFloors] = useState<Floor[]>([]);
  const [currentStep, setCurrentStep] = useState<'form' | 'preview' | 'summary' | 'creating'>('form');
  const [createParking, setCreateParking] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [summaryData, setSummaryData] = useState<{
    name: string;
    description: string;
    totalFloors: number;
    unitsPerFloor: number;
    floorPrefix: string;
  } | null>(null);
  const [creationProgress, setCreationProgress] = useState<{
    step: 'idle' | 'creating-block' | 'creating-units' | 'creating-parking' | 'updating-stats' | 'completed';
    message: string;
    progress: number;
    showSuccess: boolean;
  }>({
    step: 'idle',
    message: '',
    progress: 0,
    showSuccess: false
  });
  const { firestoreUser, refreshWorkspaceStats } = useAuth();

  // Tour functionality removed

  const generateFloors = (blockName: string, prefix: string, totalFloors: number, unitsPerFloor: number): Floor[] => {
    return Array.from({ length: totalFloors }, (_, index) => {
      const floorNumber = index + 1;
      const name = prefix ? `${prefix}${floorNumber}` : `${floorNumber}`;
      const slug = `${blockName.toLowerCase()}-${prefix ? prefix.toLowerCase() : ''}${floorNumber}`;
      
      // Generate apartment units for this floor
      const apartmentUnits = Array.from({ length: unitsPerFloor }, (_, unitIndex) => {
        const unitNumber = unitIndex + 1;
        const apartmentUnitNumber = `${floorNumber}${unitNumber}`; // e.g., 11, 12, 13, 14 for floor 1
        
        return {
          number: apartmentUnitNumber,
          name: `Unit ${apartmentUnitNumber}`,
          slug: `${slug}-unit-${apartmentUnitNumber}`
        };
      });
      
      return {
        name,
        slug,
        description: '',
        apartmentUnits
      };
    });
  };

  // Extract floor prefix and total floors from existing floors
  const extractFloorInfo = (floors: Floor[]) => {
    if (!floors || floors.length === 0) {
      return { prefix: '', totalFloors: 0 };
    }

    const totalFloors = floors.length;
    
    // Try to extract prefix from the first floor name
    const firstFloor = floors[0];
    if (firstFloor.name) {
      // Check if the name ends with a number
      const match = firstFloor.name.match(/^(.+?)(\d+)$/);
      if (match) {
        const prefix = match[1];
        // Verify this prefix works for all floors
        const isValidPrefix = floors.every((floor, index) => {
          const expectedName = `${prefix}${index + 1}`;
          return floor.name === expectedName;
        });
        if (isValidPrefix) {
          return { prefix, totalFloors };
        }
      }
    }

    // If no prefix found, check if floors are just numbers
    const isNumberOnly = floors.every((floor, index) => {
      return floor.name === `${index + 1}`;
    });

    if (isNumberOnly) {
      return { prefix: '', totalFloors };
    }

    return { prefix: '', totalFloors };
  };

  // Initialize form with existing data when modal opens
  useEffect(() => {
    if (visible && initialValues) {
      const existingFloors = initialValues.floors || [];
      const { prefix, totalFloors } = extractFloorInfo(existingFloors);
      
      form.setFieldsValue({
        name: initialValues.name,
        description: initialValues.description,
        floorPrefix: prefix,
        totalFloors: totalFloors
      });

      // Set existing floors for display
      setGeneratedFloors(existingFloors);
    } else if (visible) {
      // Reset form for new block
      form.resetFields();
      setGeneratedFloors([]);
    }
  }, [visible, initialValues, form]);

  const handleGenerateFloors = () => {
    const blockName = form.getFieldValue('name');
    const floorPrefix = form.getFieldValue('floorPrefix');
    const totalFloors = form.getFieldValue('totalFloors');
    const unitsPerFloor = form.getFieldValue('unitsPerFloor');
    if (blockName && totalFloors && unitsPerFloor) {
      const newFloors = generateFloors(blockName, floorPrefix || '', totalFloors, unitsPerFloor);
      setGeneratedFloors(newFloors);
      
      // Store form values for summary step
      setSummaryData({
        name: blockName || '',
        description: form.getFieldValue('description') || '',
        totalFloors: totalFloors || 0,
        unitsPerFloor: unitsPerFloor || 0,
        floorPrefix: floorPrefix || ''
      });
      
      setCurrentStep('preview');
    }
  };

  const handleCreate = () => {
    // Use the already stored summary data from handleGenerateFloors
    if (!summaryData) {
      console.warn('No summary data available, calculating from generated floors...');
      
      // Fallback: calculate from generated floors
      if (generatedFloors.length > 0) {
        const firstFloor = generatedFloors[0];
        const totalFloors = generatedFloors.length;
        const unitsPerFloor = firstFloor.apartmentUnits?.length || 0;
        
        // Try to extract prefix from floor names
        let floorPrefix = '';
        if (firstFloor.name) {
          const match = firstFloor.name.match(/^(.+?)(\d+)$/);
          if (match) {
            floorPrefix = match[1];
          }
        }
        
        setSummaryData({
          name: form.getFieldValue('name') || 'Block',
          description: form.getFieldValue('description') || '',
          totalFloors,
          unitsPerFloor,
          floorPrefix
        });
        
        console.log('Calculated summary data from floors:', {
          totalFloors,
          unitsPerFloor,
          floorPrefix
        });
      } else {
        console.error('No generated floors available. Please generate floors first.');
        return;
      }
    }
    
    console.log('Using summary data:', summaryData);
    console.log('Generated floors:', generatedFloors);
    
    // Move to summary step
    setCurrentStep('summary');
  };

  const handleConfirmCreation = async () => {
    try {
      if (!summaryData) {
        throw new Error('Summary data not available');
      }

      setIsCreating(true);
      setCurrentStep('creating');
      setCreationProgress({
        step: 'creating-block',
        message: 'Creating block...',
        progress: 10,
        showSuccess: false
      });
      
      // Use stored summary data instead of form validation
      const { name, description, totalFloors, floorPrefix, unitsPerFloor } = summaryData;

      // Generate floors if not already generated
      let floors: Floor[];
      if (generatedFloors.length > 0) {
        floors = generatedFloors.map((floor) => ({
          ...floor,
          description: floor.description || ''
        }));
      } else {
        floors = generateFloors(name, floorPrefix || '', totalFloors, unitsPerFloor);
      }

      // Step 1: Create the block record
      const blockData = {
        name: name || '',
        description: description || '',
        floors: floors
      };

      // Call the parent onSubmit to create the block
      const blockId = await onSubmit({
        block: blockData,
        floors: []
      });

      if (!blockId) {
        throw new Error('Failed to create block');
      }

      setCreationProgress({
        step: 'creating-block',
        message: 'Block created successfully!',
        progress: 30,
        showSuccess: true
      });

      if (!firestoreUser) {
        throw new Error('User not authenticated');
      }

      // Step 2: Create all apartment units
      setCreationProgress({
        step: 'creating-units',
        message: 'Creating apartment units...',
        progress: 40,
        showSuccess: false
      });

      const apartmentUnits: Omit<ApartmentUnit, 'id' | 'workspace' | 'activityRecords'>[] = [];
      floors.forEach(floor => {
        floor.apartmentUnits?.forEach(unit => {
          apartmentUnits.push({
            number: unit.number,
            status: 'vacant',
            block: {
              id: blockId,
              name: name
            },
            floor: {
              name: floor.name,
              slug: floor.slug
            },
            description: `Unit ${unit.number} on ${floor.name}`
          });
        });
      });

      // Create apartment units in bulk with progress tracking
      await addApartmentUnitsBulk(apartmentUnits, firestoreUser, (uploaded, total) => {
        const progress = 40 + Math.round((uploaded / total) * 30); // 40% to 70%
        setCreationProgress({
          step: 'creating-units',
          message: `Creating apartment units... ${uploaded}/${total}`,
          progress,
          showSuccess: false
        });
      });

      setCreationProgress({
        step: 'creating-units',
        message: 'Apartment units created successfully!',
        progress: 70,
        showSuccess: true
      });

      // Step 3: Create parking units if checkbox is checked
      if (createParking) {
        setCreationProgress({
          step: 'creating-parking',
          message: 'Creating parking spaces...',
          progress: 75,
          showSuccess: false
        });

        const parkingUnits: Omit<Parking, 'id' | 'workspace' | 'activityRecords'>[] = [];
        floors.forEach(floor => {
          floor.apartmentUnits?.forEach(unit => {
            parkingUnits.push({
              number: `${name}-${unit.number}`, // Block name prefix + apartment unit number
              type: 'car',
              status: 'available',
              block: {
                id: blockId,
                name: name
              },
              apartmentUnit: {
                id: '', // Will be filled by the system
                number: unit.number
              },
              description: `Parking for Unit ${unit.number} on ${floor.name}`
            });
          });
        });

        await addParkingBulk(parkingUnits, firestoreUser, (uploaded, total) => {
          const progress = 75 + Math.round((uploaded / total) * 20); // 75% to 95%
          setCreationProgress({
            step: 'creating-parking',
            message: `Creating parking spaces... ${uploaded}/${total}`,
            progress,
            showSuccess: false
          });
        });

        setCreationProgress({
          step: 'creating-parking',
          message: 'Parking spaces created successfully!',
          progress: 95,
          showSuccess: true
        });
      }

      // Step 4: Update workspace stats
      setCreationProgress({
        step: 'updating-stats',
        message: 'Updating workspace statistics...',
        progress: 96,
        showSuccess: false
      });

      // Calculate totals for stats update
      const totalUnits = floors.reduce((sum, floor) => sum + (floor.apartmentUnits?.length || 0), 0);
      const totalParkings = createParking ? totalUnits : 0;

      // Update workspace stats after successful creation
      if (firestoreUser) {
        try {
          // Initialize block stats first
          await initializeBlockStats(firestoreUser, blockId, name);
          
          // Update block-specific stats with the actual counts
          await incrementBlockCount(firestoreUser, blockId, 'totalApartmentUnits', totalUnits);
          if (createParking) {
            await incrementBlockCount(firestoreUser, blockId, 'totalParkings', totalParkings);
          }
          
          // Refresh workspace stats in auth context for pagination
          await refreshWorkspaceStats();
          
          console.log('Workspace stats updated successfully:', {
            blockId,
            blockName: name,
            totalUnits,
            totalParkings,
            createParking
          });

          setCreationProgress({
            step: 'updating-stats',
            message: 'Workspace statistics updated successfully!',
            progress: 98,
            showSuccess: true
          });
        } catch (error) {
          console.warn('Failed to update workspace stats:', error);
          // Don't fail the entire operation if stats update fails
          // The user will still see success, but stats might be outdated
          
          setCreationProgress({
            step: 'updating-stats',
            message: 'Workspace statistics update failed, but creation was successful',
            progress: 98,
            showSuccess: false
          });
        }
      }

      // Final success state
      setCreationProgress({
        step: 'completed',
        message: `Block "${name}" with ${totalFloors} floors, ${totalUnits} apartment units${createParking ? ` and ${totalParkings} parking spaces` : ''} created successfully!`,
        progress: 100,
        showSuccess: true
      });

      setIsCreating(false);

    } catch (error) {
      console.error('Error creating block:', error);
      setIsCreating(false);
      
      // Show error in progress
      setCreationProgress({
        step: 'idle',
        message: `Failed to create block: ${error instanceof Error ? error.message : 'Unknown error'}`,
        progress: 0,
        showSuccess: false
      });
    }
  };

  const handleDeleteFloor = (index: number) => {
    const updatedFloors = generatedFloors.filter((_, i) => i !== index);
    setGeneratedFloors(updatedFloors);
  };

  const handleDeleteAllFloors = () => {
    setGeneratedFloors([]);
    setCurrentStep('form');
    form.setFieldsValue({
      floorPrefix: '',
      totalFloors: undefined,
      unitsPerFloor: undefined
    });
  };

  const handleModalCancel = () => {
    form.resetFields();
    setGeneratedFloors([]);
    setCurrentStep('form');
    setCreateParking(false);
    setSummaryData(null);
    setCreationProgress({
      step: 'idle',
      message: '',
      progress: 0,
      showSuccess: false
    });
    onCancel();
  };

  const hasFloors = generatedFloors.length > 0;

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={handleModalCancel}
      footer={[
        <Button key="cancel" onClick={handleModalCancel} disabled={isCreating}>
          {t('common:actions.cancel')}
        </Button>,
        currentStep === 'form' && (
          <Button key="preview" type="primary" onClick={handleGenerateFloors}>
            {t('blocks.actions.previewFloorPlan')}
          </Button>
        ),
        currentStep === 'preview' && (
          <Button key="create" type="primary" onClick={handleCreate}>
            {t('common:actions.create')}
          </Button>
        ),
        currentStep === 'summary' && (
          <Button key="confirm" type="primary" onClick={handleConfirmCreation}>
            {t('common:actions.confirm')}
          </Button>
        )
      ]}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
      >
        {currentStep === 'form' && (
          <>
            <div id="block-basic-info-section">
              <Form.Item
                name="name"
                label={t('blocks.details.attributes.name')}
                rules={[{ required: true, message: t('common:validation.fieldRequired') }]}
              >
                <Input id="block-name-input" />
              </Form.Item>

              <Form.Item
                name="description"
                label={t('blocks.details.attributes.description')}
              >
                <Input.TextArea id="block-description-input" rows={4} />
              </Form.Item>
            </div>

            {/* Show floor prefix and total floors only when no floors exist */}
            {!hasFloors && (
              <div id="block-structure-section">
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name="totalFloors"
                      label={t('blocks.details.attributes.totalFloors')}
                      rules={[{ required: true, message: t('common:validation.fieldRequired') }]}
                    >
                      <InputNumber id="block-total-floors-input" min={1} max={100} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="unitsPerFloor"
                      label={t('blocks.details.attributes.unitsPerFloor')}
                      rules={[{ required: true, message: t('common:validation.fieldRequired') }]}
                    >
                      <InputNumber id="block-units-per-floor-input" min={1} max={50} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="floorPrefix"
                      label={t('blocks.details.attributes.apartmentUnitPrefix')}
                    >
                      <Input id="block-floor-prefix-input" placeholder={t('blocks.details.attributes.apartmentUnitPrefixPlaceholder')} />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            )}
          </>
        )}

        {currentStep === 'preview' && (
          <>
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h4 style={{ margin: 0 }}>{t('blocks.details.attributes.floorPlan')}</h4>
                <Popconfirm
                  title={t('blocks.messages.deleteAllFloorsConfirm')}
                  onConfirm={handleDeleteAllFloors}
                  okText={t('common:actions.yes')}
                  cancelText={t('common:actions.no')}
                >
                  <Button 
                    type="text" 
                    danger 
                    icon={<DeleteOutlined />}
                    size="small"
                  >
                    {t('blocks.actions.deleteAllFloors')}
                  </Button>
                </Popconfirm>
              </div>

              {/* Parking Creation Checkbox */}
              <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#f6f8fa', borderRadius: '6px', border: '1px solid #e1e4e8' }}>
                <Form.Item
                  name="createParking"
                  valuePropName="checked"
                  style={{ marginBottom: 0 }}
                >
                  <Checkbox 
                    checked={createParking}
                    onChange={(e) => setCreateParking(e.target.checked)}
                  >
                    {t('blocks.actions.createParkingAutomatically')}
                  </Checkbox>
                </Form.Item>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', marginLeft: '24px' }}>
                  {t('blocks.messages.parkingCreationInfo')}
                </div>
              </div>
              
              {/* Tower Visualization */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column-reverse', // Show ground floor at bottom
                gap: '2px',
                border: '2px solid #d9d9d9',
                borderRadius: '8px',
                padding: '16px',
                backgroundColor: '#fafafa',
                maxHeight: '400px',
                overflowY: 'auto'
              }}>
                {generatedFloors.map((floor, floorIndex) => (
                  <div key={floorIndex} style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px',
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    border: '1px solid #e8e8e8'
                  }}>
                    {/* Floor Label */}
                    <div style={{ 
                      minWidth: '80px',
                      fontWeight: 'bold',
                      color: '#1890ff',
                      fontSize: '14px',
                      textAlign: 'center'
                    }}>
                      {floor.name}
                    </div>
                    
                    {/* Apartment Units */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '4px',
                      flexWrap: 'wrap'
                    }}>
                      {floor.apartmentUnits?.map((unit, unitIndex) => (
                        <div key={unitIndex} style={{
                          width: '40px',
                          height: '30px',
                          backgroundColor: '#f0f0f0',
                          border: '1px solid #d9d9d9',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          fontWeight: '500',
                          color: '#595959',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        title={`${unit.name} (${unit.slug})`}
                        >
                          {unit.number}
                        </div>
                      )) || (
                        // Fallback if no apartment units
                        <div style={{
                          width: '40px',
                          height: '30px',
                          backgroundColor: '#f0f0f0',
                          border: '1px solid #d9d9d9',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          color: '#999'
                        }}>
                          -
                        </div>
                      )}
                    </div>
                    
                    {/* Floor Actions */}
                    <div style={{ marginLeft: 'auto' }}>
                      <Popconfirm
                        title={t('blocks.messages.deleteFloorConfirm')}
                        onConfirm={() => handleDeleteFloor(floorIndex)}
                        okText={t('common:actions.yes')}
                        cancelText={t('common:actions.no')}
                      >
                        <Button 
                          type="text" 
                          danger 
                          icon={<DeleteOutlined />}
                          size="small"
                          style={{ padding: 0, minWidth: 'auto' }}
                        />
                      </Popconfirm>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Legend */}
              <div style={{ 
                marginTop: '12px', 
                fontSize: '12px', 
                color: '#666',
                textAlign: 'center'
              }}>
                <span style={{ fontWeight: '500' }}>Legend:</span> Each box represents an apartment unit. 
                Numbers show the unit identifier (Floor + Unit).
              </div>
            </div>
          </>
        )}

        {/* Summary Step */}
        {currentStep === 'summary' && summaryData && (
          <div style={{ 
            marginTop: 16, 
            padding: '24px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px', 
            border: '1px solid #e9ecef'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <h3 style={{ color: '#1890ff', marginBottom: '8px' }}>
                {t('blocks.messages.creationSummary.title')}
              </h3>
              <p style={{ color: '#666', fontSize: '14px' }}>
                {t('blocks.messages.creationSummary.subtitle')}
              </p>
            </div>

            {/* Summary Details */}
            <div style={{ 
              backgroundColor: '#fff', 
              padding: '20px', 
              borderRadius: '6px', 
              border: '1px solid #d9d9d9',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  backgroundColor: '#1890ff', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  marginRight: '16px'
                }}>
                  <span style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>
                    {summaryData.name.charAt(0)?.toUpperCase() || 'B'}
                  </span>
                </div>
                <div>
                  <h4 style={{ margin: 0, color: '#1890ff' }}>
                    {summaryData.name || 'Block Name'}
                  </h4>
                  <p style={{ margin: 0, color: '#666', fontSize: '12px' }}>
                    {summaryData.description || 'No description'}
                  </p>
                </div>
              </div>

              <Row gutter={16}>
                <Col span={8}>
                  <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#f6ffed', borderRadius: '6px', border: '1px solid #b7eb8f' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                      {summaryData.totalFloors}
                    </div>
                    <div style={{ fontSize: '12px', color: '#52c41a' }}>
                      {t('blocks.messages.creationSummary.floors')}
                    </div>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#e6f7ff', borderRadius: '6px', border: '1px solid #91d5ff' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                      {summaryData.unitsPerFloor}
                    </div>
                    <div style={{ fontSize: '12px', color: '#1890ff' }}>
                      {t('blocks.messages.creationSummary.unitsPerFloor')}
                    </div>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#fff7e6', borderRadius: '6px', border: '1px solid #ffd591' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fa8c16' }}>
                      {summaryData.totalFloors * summaryData.unitsPerFloor}
                    </div>
                    <div style={{ fontSize: '12px', color: '#fa8c16' }}>
                      {t('blocks.messages.creationSummary.totalUnits')}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Parking Summary */}
              {createParking && (
                <div style={{ 
                  marginTop: '16px', 
                  padding: '16px', 
                  backgroundColor: '#f6ffed', 
                  borderRadius: '6px', 
                  border: '1px solid #b7eb8f',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px', fontSize: '16px' }} />
                  <span style={{ color: '#52c41a', fontSize: '14px' }}>
                    {t('blocks.messages.creationSummary.parkingInfo', {
                      count: summaryData.totalFloors * summaryData.unitsPerFloor
                    })}
                  </span>
                </div>
              )}

              {/* Confirmation Message */}
              <div style={{ 
                marginTop: '20px', 
                padding: '16px', 
                backgroundColor: '#fff2e8', 
                borderRadius: '6px', 
                border: '1px solid #ffbb96',
                textAlign: 'center'
              }}>
                <p style={{ margin: 0, color: '#d46b08', fontSize: '14px' }}>
                  {t('blocks.messages.creationSummary.confirmationMessage', {
                    name: summaryData.name || 'Block',
                    floors: summaryData.totalFloors,
                    unitsPerFloor: summaryData.unitsPerFloor,
                    totalUnits: summaryData.totalFloors * summaryData.unitsPerFloor,
                    parking: createParking ? summaryData.totalFloors * summaryData.unitsPerFloor : 0
                  })}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Creation Progress Display */}
        {currentStep === 'creating' && creationProgress.step !== 'idle' && (
          <div style={{ 
            marginTop: 24, 
            padding: '20px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px', 
            border: '1px solid #e9ecef'
          }}>
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ margin: 0, color: '#1890ff' }}>
                {creationProgress.step === 'completed' ? t('blocks.messages.creationProgress.completed') : t('blocks.messages.creationProgress.title')}
              </h4>
            </div>

            {/* Progress Bar */}
            <div style={{ marginBottom: 16 }}>
              <Progress 
                percent={creationProgress.progress} 
                status={creationProgress.step === 'completed' ? 'success' : 'active'}
                strokeColor={creationProgress.step === 'completed' ? '#52c41a' : '#1890ff'}
                format={(percent) => `${percent}%`}
              />
            </div>

            {/* Step Indicators */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '8px',
                color: creationProgress.step === 'creating-block' || creationProgress.step === 'creating-units' || creationProgress.step === 'creating-parking' || creationProgress.step === 'updating-stats' || creationProgress.step === 'completed' ? '#52c41a' : '#d9d9d9'
              }}>
                <CheckCircleOutlined style={{ marginRight: '8px', fontSize: '16px' }} />
                <span>{t('blocks.messages.creationProgress.steps.createBlock')}</span>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '8px',
                color: creationProgress.step === 'creating-units' || creationProgress.step === 'creating-parking' || creationProgress.step === 'updating-stats' || creationProgress.step === 'completed' ? '#52c41a' : '#d9d9d9'
              }}>
                <CheckCircleOutlined style={{ marginRight: '8px', fontSize: '16px' }} />
                <span>{t('blocks.messages.creationProgress.steps.createUnits')}</span>
              </div>
              {createParking && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '8px',
                  color: creationProgress.step === 'creating-parking' || creationProgress.step === 'updating-stats' || creationProgress.step === 'completed' ? '#52c41a' : '#d9d9d9'
                }}>
                  <CheckCircleOutlined style={{ marginRight: '8px', fontSize: '16px' }} />
                  <span>{t('blocks.messages.creationProgress.steps.createParking')}</span>
                </div>
              )}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '8px',
                color: creationProgress.step === 'updating-stats' || creationProgress.step === 'completed' ? '#52c41a' : '#d9d9d9'
              }}>
                <CheckCircleOutlined style={{ marginRight: '8px', fontSize: '16px' }} />
                <span>{t('blocks.messages.creationProgress.steps.updateStats')}</span>
              </div>
            </div>

            {/* Current Status Message */}
            <div style={{ 
              padding: '12px', 
              backgroundColor: creationProgress.showSuccess ? '#f6ffed' : '#fff', 
              border: `1px solid ${creationProgress.showSuccess ? '#b7eb8f' : '#d9d9d9'}`,
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {creationProgress.showSuccess && (
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px', fontSize: '16px' }} />
                )}
                <span style={{ color: creationProgress.showSuccess ? '#52c41a' : '#595959' }}>
                  {creationProgress.message}
                </span>
              </div>
              {creationProgress.step === 'completed' && (
                <Button 
                  type="primary" 
                  size="small"
                  onClick={() => {
                    // Reset form and close modal
                    form.resetFields();
                    setGeneratedFloors([]);
                    setCurrentStep('form');
                    setCreateParking(false);
                    setSummaryData(null);
                    setCreationProgress({
                      step: 'idle',
                      message: '',
                      progress: 0,
                      showSuccess: false
                    });
                    onCancel();
                  }}
                >
                  Close
                </Button>
              )}
            </div>
          </div>
        )}
      </Form>
    </Modal>
  );
};

